import React, {useRef} from 'react';
import styled from 'styled-components';
import {useRect} from '../../../shared';
import {getOptImage} from 'epic-ue-shared';
import {getBowserState} from '@epic-core/common';

type RectResult = {
    bottom: number;
    height: number;
    left: number;
    right: number;
    top: number;
    width: number;
};

const handleDimensions = (dimension) => {
    if (dimension !== undefined) {
        return `${dimension}px`;
    } else {
        return `100%`;
    }
};

/**
 *
 * @param dimension: number
 * @param bounds: RectResult
 *
 * if dimensions are provided, use those to set the height
 * else use the bounds of the width to determine the width and set the height the same value
 *
 */
const handleSquareDimensions = (dimension, bounds) => {
    if (dimension !== undefined) {
        return `${dimension}px`;
    } else if (bounds && bounds.width) {
        return `${bounds.width}px`;
    } else {
        return `100%`;
    }
};

const setAlignment = (alignment) => {
    if (alignment) {
        switch (alignment) {
            case 'left':
                return '0 0';
            case 'right':
                return '100% 0';
            case 'center':
                return '50% 0';
            default:
                return '50% 0';
        }
    } else {
        return '50% 0';
    }
};

const ImgWrapper = styled.div<{
    width?: number;
    height?: number;
    bounds: RectResult;
    alignment: string;
    fit: string;
}>`
    width: ${({width}) => handleDimensions(width)};
    height: ${({height}) => handleDimensions(height)};
    position: relative;
    overflow: hidden;

    &.ratio-square {
        height: ${({height, bounds}) => handleSquareDimensions(height, bounds)};
        width: ${({width}) => handleDimensions(width)};
        .ratio-keeper {
            aspect-ratio: 1 / 1;
        }
    }

    &.with-gradient {
        &:after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 80%;
            opacity: 0.9;
            background: linear-gradient(
                360deg,
                ${(p) => p.theme.palette.background.default} 0%,
                rgba(0, 0, 0, 0) 100%
            );
        }
    }

    &.ratio-rect {
        width: ${({width}) => handleDimensions(width)};
        .ratio-keeper {
            aspect-ratio: 16 / 9;
        }
    }

    .ratio-keeper {
        position: relative;
    }

    .lazy-loading {
        width: 100%;
        height: 100%;
        img {
            opacity: 0;
        }
    }

    img {
        width: 100%;
        height: 100%;
        object-fit: ${(p) => p.fit};
        object-position: ${({alignment}) => setAlignment(alignment)};
        transition: 0.3s ease;
        opacity: 1;
    }
`;

export type ImageRatioOptions = 'square' | 'rect' | 'custom';

export interface ImageProps {
    ratio?: ImageRatioOptions;
    src?: string;
    alt?: string;
    role?: string;
    title?: string;
    width?: number;
    height?: number;
    aria?: string;
    fit?: string;
    disableRatioHelper?: boolean;
    gradient?: boolean;
    alignment?: string;
    noLazy?: boolean;
    optMaxWidth?: number;
}

export const Image = ({
    ratio = 'rect',
    src,
    alt,
    role,
    title,
    width,
    height,
    gradient = false,
    fit = 'cover',
    alignment = 'center',
    aria,
    disableRatioHelper,
    noLazy,
    optMaxWidth
}: ImageProps): JSX.Element => {
    const {mobile, tablet} = getBowserState();
    const imgRef = useRef<HTMLDivElement>(null);
    const imgRect = useRect(imgRef);
    const ariaRole = role ? role : 'img';
    let imgWidth = width;

    //Set the width the same as the height when square and only height is provided.
    if (ratio === 'square') {
        if (!imgWidth && height && height > 10) {
            imgWidth = height;
        }
    }

    let imageWidth: number;
    if (imgWidth) {
        imageWidth = imgWidth + 200; //User passed in fixed width so we can use that as the max width.
    } else if (imgRect && imgRect.width) {
        imageWidth = imgRect.width + 200; //Since we are getting the bounds, we can try using that.
    } else if (mobile || tablet) {
        imageWidth = 1200; //Makes sure the image looks crisp on tablets and landscape phones.
    } else {
        imageWidth = 2100; //If all else fails for dekstop, just render it at 1080p.
    }

    let imageElem: JSX.Element = <div key={'ue-image-no-img-src'} />;
    if (src) {
        imageElem = (
            <img
                loading={noLazy ? undefined : 'lazy'}
                src={getOptImage({url: src, width: optMaxWidth || imageWidth})}
                className={`image-element`}
                title={title ? title : ''}
                alt={`${alt ? alt : ''}`}
            />
        );
    }

    let imageWrap: JSX.Element = (
        <div className="ratio-keeper">
            {imageElem}
        </div>
    );

    if (disableRatioHelper && src) {
        imageWrap = imageElem;
    }

    return (
        <ImgWrapper
            ref={imgRef}
            className={`image-wrapper ratio-${ratio || ''}  ${gradient ? 'with-gradient' : ''}`}
            bounds={imgRect}
            alignment={alignment}
            width={imgWidth}
            fit={fit}
            role={ariaRole}
            aria-label={`${alt ? alt : ''}`}
            aria-describedby={`${aria ? aria : ''}`}
            height={height}
        >
            {imageWrap}
        </ImgWrapper>
    );
};
